import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  ButtonGroup,
  Stack,
} from "@mui/material";
import { Refresh as RefreshIcon, Search } from "@mui/icons-material";
import Loader from "@jumbo/components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { usePagination } from "app/hooks/usePagination";
import { Table as CustomTable } from "app/components";
import moment from "moment";
import { useSnackbar } from "notistack";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { getFlipkartProductReport } from "app/redux/slice/flipkartSlice";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const FlipkartProductReport = () => {
  const dispatch = useDispatch();
  const flipkartData = useSelector((state) => state?.flipkart);
  const products = flipkartData?.productReport?.list ?? [];
  const { enqueueSnackbar } = useSnackbar();
  const [dateRange, setDateRange] = useState([null, null]);
  const [tempSearchTerm, setTempSearchTerm] = useState("");

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("finalPL");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "flipkart-product-report" });

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, order, orderBy]);

  const fetchReport = () => {
    const params = {
      page,
      perPage: rowsPerPage,
    };
    if (orderBy !== "") {
      params.sort = order === "asc" ? 1 : -1;
      params.field = orderBy;
    }
    dispatch(getFlipkartProductReport(params));
  };

  const handleApplyFilter = () => {
    const params = {
      page,
      perPage: rowsPerPage,
    };
    if (orderBy !== "") {
      params.sort = order === "asc" ? 1 : -1;
      params.field = orderBy;
    }
    if (tempSearchTerm !== "") {
      params.search = tempSearchTerm.trim();
    }
    if ((dateRange[0] && !dateRange[1]) || (!dateRange[0] && dateRange[1])) {
      enqueueSnackbar("Please select both start and end date", {
        variant: "error",
      });
      return;
    }
    if (dateRange[0] && dateRange[1]) {
      params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
      params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }
    dispatch(getFlipkartProductReport(params));
  };

  const columns = [
    {
      id: "skuName",
      label: "SKU",
      sort: true,
      render: (row) => row?.skuName || row?._id || "-",
    },
    {
      id: "itemName",
      label: "Item Name",
      sort: true,
      render: (row) => row?.itemName || "-",
    },
    {
      id: "productName",
      label: "Product Name",
      sort: true,
      render: (row) => row?.productName || "-",
    },
    {
      id: "costPerUnit",
      label: "Cost Per Unit",
      sort: true,
      render: (row) => row?.costPerUnit,
    },
    {
      id: "totalOrders",
      label: "Total Orders",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "totalNetUnits",
      label: "Total Net Units",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "totalReturned",
      label: "Total Returns",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "returnPercentage",
      label: "Return %",
      sort: true,
      render: (row) => (
        <span>
          {Number(row?.returnPercentage || 0).toFixed(2)}%
        </span>
      ),
    },
    {
      id: "netEarnings",
      label: "Total Settlement",
      sort: true,
      isTotal: true,
      format: (v) => v?.toFixed(2),
    },
    {
      id: "totalCostPrice",
      label: "Total Cost",
      sort: true,
      isTotal: true,
      format: (v) => v?.toFixed(2),
    },
    {
      id: "finalPL",
      label: "Final P/L",
      sort: true,
      minWidth: 130,
      render: (row) => {
        const value = Math.abs(Number(row?.finalPL || 0));
        const isProfit = Number(row?.finalPL || 0) >= 0;
        return (
          <span
            style={{
              color: isProfit ? "green" : "red",
              fontWeight: "bold",
            }}
          >
            {isProfit ? "+" : "-"}
            {value.toFixed(2)}
          </span>
        );
      },
      isTotal: true,
      format: (v) => v?.toFixed(2),
    },
  ];

  const sortData = (o, ob) => {
    setOrder(o);
    setOrderBy(ob);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Flipkart Product Report
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by SKU..."
                value={tempSearchTerm}
                onChange={(e) => setTempSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2.75}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={dateRange}
                  onChange={setDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={3.25}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ButtonGroup size="small" variant="outlined">
                  {QUICK_RANGES.map((r) => (
                    <Button key={r.key} onClick={() => setDateRange(r.getRange())}>
                      {r.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={1.5}>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                disabled={flipkartData?.productReport?.loading}
                startIcon={
                  flipkartData?.productReport?.loading ? (
                    <CircularProgress size={16} />
                  ) : null
                }
                fullWidth
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Button
                variant="outlined"
                onClick={() => {
                  setTempSearchTerm("");
                  setDateRange([null, null]);
                  handleChangePage(0, 0);
                  setOrderBy("finalPL");
                  setOrder("asc");
                  fetchReport();
                }}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {flipkartData?.productReport?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <CustomTable
              columns={columns}
              rows={products}
              page={page}
              rowsPerPage={rowsPerPage}
              count={flipkartData?.productReport?.total || products.length}
              Order={order}
              OrderBy={orderBy}
              sortData={sortData}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={flipkartData?.productReport?.loading}
              showTotalInFooter={true}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FlipkartProductReport;
